<template>
    <div>
        <v-row>
            <v-col cols="12">
                <v-card class="mb-3">
                    <v-card-title>Ads Configuration</v-card-title>
                    <v-card-text>
                        <p>Sidebar (460x740)</p>
                        <input
                            type="file"
                            class="d-none"
                            ref="uploader"
                            accept="image/*"
                            @change="selectFile('sidebarAdImg')">
                        <v-text-field
                            v-model="sidebarAdImg"
                            :rules="urlRule"
                            :disabled="loadingAdConfig"
                            label="Image URL"
                            prepend-icon="mdi-cloud-upload"
                            @click:prepend="$refs.uploader.click()">
                        </v-text-field>
                        <div class="d-flex flex-row justify-space-between align-center">
                            <v-text-field
                                v-model="sidebarAdUrl"
                                :rules="urlRule"
                                label="Ad URL">
                            </v-text-field>
                            <v-btn
                                class="ml-4 mb-2"
                                color="primary"
                                :disabled="loadingAdConfig"
                                @click="saveAdConfig">
                                Save
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>

                <v-card class="mb-3">
                    <v-card-title>Sync Companies Utils</v-card-title>
                    <v-card-text>
                        <p class="text--primary">
                            Due to the increased size of collection <strong>companies</strong>, iterating through each
                            document in the Firestore database might lead to higher hosting costs. This sync button is
                            here to prevent us from paying more. However, use it with caution!
                        </p>
                        <p class="text--primary">
                            Recommended to sync <strong>not more than once a day</strong>.
                        </p>
                        <div class="d-sm-flex justify-space-between align-center">
                            <p class="text--secondary ma-sm-0" v-if="companiesUtils">
                                <v-icon small>mdi-clock-outline</v-icon> Last synced on
                                <strong>{{ new Date(Number(companiesUtils.timestamp.seconds) * 1000).toLocaleString('en-UK') }}</strong>
                            </p>

                            <v-spacer />

                            <v-btn
                                color="primary"
                                :disabled="loading"
                                @click="syncCollectionUtils(collNameCompanies, 'companies')">
                                <v-progress-circular
                                    v-show="loading"
                                    :width="3"
                                    :size="16"
                                    class="mr-2"
                                    indeterminate></v-progress-circular>
                                <v-icon v-show="!loading" left>mdi-sync</v-icon>Sync Companies Utils
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>

                <v-card class="mb-3">
                    <v-card-title>Sync Comments Utils</v-card-title>
                    <v-card-text>
                        <p class="text--primary">
                            Due to the constantly increasing size of collection <strong>comments</strong>, iterating
                            through each document in the Firestore database might lead to higher hosting costs.
                            This sync button is here to prevent us from paying more. However, use it with caution!
                        </p>
                        <p class="text--primary">
                            Recommended to sync <strong>not more than once a week</strong>.
                        </p>
                        <div class="d-sm-flex justify-space-between align-center">
                            <p class="text--secondary ma-sm-0" v-if="commentsUtils">
                                <v-icon small>mdi-clock-outline</v-icon> Last synced on
                                <strong>{{ new Date(Number(commentsUtils.timestamp.seconds) * 1000).toLocaleString('en-UK') }}</strong>
                            </p>

                            <v-spacer />

                            <v-btn
                                color="primary"
                                :disabled="loadingComments"
                                @click="syncCollectionUtils(collNameComments, 'comments')">
                                <v-progress-circular
                                    v-show="loadingComments"
                                    :width="3"
                                    :size="16"
                                    class="mr-2"
                                    indeterminate></v-progress-circular>
                                <v-icon v-show="!loadingComments" left>mdi-sync</v-icon>Sync Comments Utils
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>

                <v-card class="mb-3">
                    <v-card-title>Backup Collection Companies</v-card-title>
                    <v-card-text>
                        <p class="text--primary">
                            Due to the increased size of collection <strong>companies</strong>, iterating through each
                            document in the Firestore database might lead to higher hosting costs. However, backing up
                            your data is of high importance.
                        </p>
                        <p class="text--primary">
                            Recommended to backup <strong>not more than once a day</strong>.
                        </p>
                        <div class="d-sm-flex justify-space-between align-center">
                            <p class="text--secondary ma-sm-0" v-if="backupDate">
                                <v-icon small>mdi-clock-outline</v-icon> Last backed up on
                                <strong>{{ new Date(Number(backupDate.backed_up_at.seconds) * 1000).toLocaleString('en-UK') }}</strong>
                            </p>

                            <v-spacer />

                            <v-btn
                                color="primary"
                                :disabled="backingUp"
                                @click="collectionBackup('companies', 'companies_backup')">
                                <v-progress-circular
                                    v-show="backingUp"
                                    :width="3"
                                    :size="16"
                                    class="mr-2"
                                    indeterminate></v-progress-circular>
                                <v-icon v-show="!backingUp" left>mdi-sync</v-icon>Backup Companies
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
            <span>{{ snackText }}</span>
            <template v-slot:action="{ attrs }">
                <v-icon v-bind="attrs" text @click="snack = false">mdi-close</v-icon>
            </template>
        </v-snackbar>

    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/storage';
import {showSnack} from "@/components/mixins/showSnack";
import {getStorageItemUrl} from "@/components/mixins/getStorageItemUrl";
const imgRegex = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;

export default {
    name: 'Settings',

    mixins: [showSnack, getStorageItemUrl],

    data() {
        return {
            loading: false,
            loadingAdConfig: false,
            loadingComments: false,
            backingUp: false,
            collNameSettings: process.env.VUE_APP_COLLECTION_SETTINGS,
            collNameCompanies: process.env.VUE_APP_COLLECTION_COMPANIES,
            collNameComments: process.env.VUE_APP_COLLECTION_COMMENTS,
            collNameCompaniesBackup: process.env.VUE_APP_COLLECTION_COMPANIES_BACKUP,
            collection: null,
            syncCompaniesData: {
                searchableCompanies: null,
                cities: null,
                length: null,

                readyForPublishing: null,
                published: null,
                red: null,
                yellow: null,
                green: null,

                publicLength: null,
                publicRed: null,
                publicYellow: null,
                publicGreen: null,

                privateLength: null,
                privateRed: null,
                privateYellow: null,
                privateGreen: null,

                sidebarAdImg: null,
                sidebarAdUrl: null,

                timestamp: null
            },
            syncCommentsUtils: {
                length: null,
                unapproved: null,

                timestamp: null
            },

            backupDate: null,

            sidebarAdImg: null,
            sidebarAdUrl: null,
            urlRule: [
                v => v ? (imgRegex.test(v) || 'URL must be valid') : true
            ]
        }
    },

    created() {
        this.getBackupDate('companies_backup');
        this.getAdConfig();
    },

    computed: {
        ...mapGetters({
            companiesUtils: 'dashboard/companiesUtils',
            commentsUtils: 'dashboard/commentsUtils'
        }),
    },

    methods: {
        ...mapActions({
            setCompaniesUtils: 'dashboard/setCompaniesUtils',
            setCommentsUtils: 'dashboard/setCommentsUtils',
            setAdConfig: 'dashboard/setAdConfig'
        }),
        async getAdConfig() {
            if (this.companiesUtils) {
                this.sidebarAdImg = this.companiesUtils.sidebarAdImg;
                this.sidebarAdUrl = this.companiesUtils.sidebarAdUrl;
            } else {
                this.sidebarAdImg = await this.getStorageItemUrl('public_images/sidebarAdImg');
                const doc = await firebase.firestore().collection(process.env.VUE_APP_COLLECTION_UTILS).doc(process.env.VUE_APP_COLLECTION_COMPANIES).get();
                this.sidebarAdUrl = doc.data().sidebarAdUrl;
            }
        },
        async saveAdConfig() {
            const { sidebarAdImg, sidebarAdUrl } = this;
            if (sidebarAdImg && sidebarAdUrl) {
                try {
                    await this.setAdConfig({ sidebarAdImg, sidebarAdUrl });
                    this.showSnack('Ads Configuration is saved', 'success');
                } catch (e) {
                    this.showSnack('Ads Configuration is not saved', 'error');
                    console.error(e);
                }
            }
        },
        selectFile(imageName) {
            let file = this.$refs.uploader.files[0];
            if (file && file.name) {
                this.loadingAdConfig = true;
                const ref = firebase.storage().ref().child(`public_images/${imageName}`);
                const vm = this;
                ref.put(file).then(snapshot => {
                    this.loadingAdConfig = false;
                    if (snapshot.state === 'success') {
                        snapshot.ref.getDownloadURL().then(downloadURL => {
                            vm[imageName] = downloadURL;
                        });
                    } else {
                        this.showSnack('Image could not be uploaded', 'error');
                    }
                    file = null;
                });
            }
        },
        /**
         * @param {String} collName - collection name === utilDocId
         * @param {String} schema - pick doc schema
         */
        async syncCollectionUtils(collName, schema) {
            try {
                const snapshot = await firebase.firestore().collection(collName).get();
                this.collection = snapshot.docs.map(doc => doc.data());

                switch(schema) {
                case 'companies':
                    await this.utilsCompaniesDocSchema();
                    break;
                case 'comments':
                    await this.utilsCommentsDocSchema();
                    break;
                }

                this.showSnack(`Collection ${collName} sync completed`, 'success');
            } catch (e) {
                console.error(e);
                this.showSnack(`Collection ${collName} sync failed`, 'error');
            } finally {
                this.loading = false;
                this.loadingComments = false;
            }
        },
        async utilsCommentsDocSchema() {
            this.loadingComments = true;
            this.syncCommentsUtils.length = this.collection.length;
            this.syncCommentsUtils.unapproved = this.collection.filter(comment => comment.approved === false).length;
            this.syncCommentsUtils.timestamp = firebase.firestore.Timestamp.fromDate(new Date());
            await this.setCommentsUtils(this.syncCommentsUtils);
        },
        async utilsCompaniesDocSchema() {
            this.loading = true;
            this.syncCompaniesData.searchableCompanies = this.collection
                .filter(company => company.published)
                .map(company => {
                    return {
                        naziv: company.naziv,
                        grad: company.grad,
                        score: company.score,
                        id: company.id
                    }
                });
            this.syncCompaniesData.cities = this.companiesUtils.cities;
            this.syncCompaniesData.length = this.collection.length;
            this.syncCompaniesData.sidebarAdImg = this.companiesUtils.sidebarAdImg || '';
            this.syncCompaniesData.sidebarAdUrl = this.companiesUtils.sidebarAdUrl || '';

            // All companies
            this.syncCompaniesData.published = this.collection.filter(company => company.published).length;
            this.syncCompaniesData.readyForPublishing = this.collection.filter(company => company.ready_for_publish).length - this.syncCompaniesData.published;
            this.syncCompaniesData.red = this.collection.filter(company => {
                return company.published && company.score !== null && 0 <= company.score && company.score <= 3;
            }).length;
            this.syncCompaniesData.yellow = this.collection.filter(company => {
                return company.published && company.score && 4 <= company.score && company.score <= 7;
            }).length;
            this.syncCompaniesData.green = this.collection.filter(company => {
                return company.published && company.score && 8 <= company.score;
            }).length;

            // Public companies
            this.syncCompaniesData.publicLength = this.collection.filter(company => company.published && company.javna).length;
            this.syncCompaniesData.publicRed = this.collection.filter(company => {
                return company.published && company.javna && company.score !== null && 0 <= company.score && company.score <= 3;
            }).length;
            this.syncCompaniesData.publicYellow = this.collection.filter(company => {
                return company.published && company.javna && company.score && 4 <= company.score && company.score <= 7;
            }).length;
            this.syncCompaniesData.publicGreen = this.collection.filter(company => {
                return company.published && company.javna && company.score && 8 <= company.score;
            }).length;

            // Private companies
            this.syncCompaniesData.privateLength = this.collection.filter(company => company.published && !company.javna).length;
            this.syncCompaniesData.privateRed = this.collection.filter(company => {
                return company.published && !company.javna && company.score !== null && 0 <= company.score && company.score <= 3;
            }).length;
            this.syncCompaniesData.privateYellow = this.collection.filter(company => {
                return company.published && !company.javna && company.score && 4 <= company.score && company.score <= 7;
            }).length;
            this.syncCompaniesData.privateGreen = this.collection.filter(company => {
                return company.published && !company.javna && company.score && 8 <= company.score;
            }).length;

            this.syncCompaniesData.timestamp = firebase.firestore.Timestamp.fromDate(new Date());
            await this.setCompaniesUtils(this.syncCompaniesData);
        },
        async collectionBackup(collName, backupCollName) {
            this.backingUp = true;
            try {
                const snapshot = await firebase.firestore().collection(collName).get();
                const destCollection = firebase.firestore().collection(backupCollName);

                const batchArray = [firebase.firestore().batch()];
                let operationCounter = 0;
                let batchIndex = 0;

                snapshot.forEach(doc => {
                    batchArray[batchIndex].set(destCollection.doc(doc.id), doc.data());
                    operationCounter++;

                    if (operationCounter === 499) {
                        batchArray.push(firebase.firestore().batch());
                        batchIndex++;
                        operationCounter = 0;
                    }
                });

                for (const batch of batchArray) {
                    await batch.commit();
                }

                await firebase.firestore().collection(this.collNameSettings).doc(backupCollName).set({
                    backed_up_at: firebase.firestore.Timestamp.fromDate(new Date)
                });
                this.showSnack(`Collection ${collName} backup completed`, 'success');
            } catch (e) {
                console.error(e);
                this.showSnack(`Collection ${collName} backup failed`, 'error');
            } finally {
                this.backingUp = false;
            }
        },
        async getBackupDate(backupCollName) {
            const doc = await firebase.firestore().collection(this.collNameSettings).doc(backupCollName).get();
            this.backupDate = doc.data();
        }
    }
}
</script>
